import React, { useCallback, useEffect, useMemo, useState } from "react";
import { filterBy } from "@progress/kendo-data-query";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Hint } from "@progress/kendo-react-labels";
import { icon } from "../../../../../../config/constants";
import { VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS } from "../../../../WorkflowVariables/workflowVariables.constants";
import {
  EXPRESSION_LABELS,
  FIELD_SELECTION_COLLAPSED_ROW_ID_PREFIX,
  WF_EXPRESSION_VARIABLE_GROUP_HEADERS,
  WF_EXPRESSION_VARIABLE_GROUP_NAMES,
} from "../../../constants/expressionBuilder.constants";
import "../../../styles/ExpressionBuilder.css";

/**
 * Builds grouped dropdown data, replacing fields in collapsed groups with a single placeholder row
 * so Kendo still renders the group header.
 * @param {Array<Object>} fields - Filtered field rows
 * @param {Set<string>} collapsedGroupKeySet - Group keys (raw `group` values) that are collapsed
 * @returns {Array<Object>} Data for DropDownList
 * @author Abhishek Dhillon
 */
const buildDropdownListData = (fields, collapsedGroupKeySet) => {
  if (!fields?.length) return fields || [];
  if (!collapsedGroupKeySet?.size) return [...fields];

  const groupOrder = [];
  const seen = new Set();
  for (const f of fields) {
    const g = f.group ?? "";
    if (!seen.has(g)) {
      seen.add(g);
      groupOrder.push(g);
    }
  }

  const out = [];
  for (const g of groupOrder) {
    const groupItems = fields.filter((f) => (f.group ?? "") === g);
    if (collapsedGroupKeySet.has(g)) {
      out.push({
        id: `${FIELD_SELECTION_COLLAPSED_ROW_ID_PREFIX}${g}`,
        name: "\u00a0",
        group: g,
        __fieldSelectionGroupCollapsed: true,
      });
    } else {
      out.push(...groupItems);
    }
  }
  return out;
};

/**
 * Stable DOM id fragment for a group toggle control
 * @param {string} groupKey - Raw group key from data
 * @returns {string} Slug for use in element ids
 * @author Abhishek Dhillon
 */
const getFieldSelectionGroupToggleIdFragment = (groupKey) =>
  String(groupKey ?? "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .toLowerCase() || "group";

/**
 * Component for field selection in expression rules
 * @param {Array} availableFields - Available workflow variables
 * @param {Object} selectedField - Selected field object
 * @param {Function} onFieldChange - Field change handler
 * @param {string} fieldType - Type of selected field
 * @returns {JSX.Element} Field selection component
 * @author Abhishek Dhillon
 * @parent RuleComponent
 */
const FieldSelection = ({ availableFields = [], selectedField, onFieldChange, fieldType }) => {
  const [filteredFields, setFilteredFields] = useState(availableFields);
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState([]);

  const collapsedGroupKeySet = useMemo(() => new Set(collapsedGroupKeys), [collapsedGroupKeys]);

  const dropdownListData = useMemo(() => buildDropdownListData(filteredFields, collapsedGroupKeySet), [filteredFields, collapsedGroupKeySet]);

  /**
   * Synchronizes filteredFields with availableFields prop changes
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    setFilteredFields(availableFields);
  }, [availableFields]);

  /**
   * Drops collapse state for groups that no longer exist in the filtered list
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    const present = new Set(filteredFields.map((f) => f.group ?? ""));
    setCollapsedGroupKeys((prev) => {
      const next = prev.filter((k) => present.has(k));
      return next.length === prev.length && next.every((k, i) => k === prev[i]) ? prev : next;
    });
  }, [filteredFields]);

  /**
   * Toggles expanded/collapsed list rows for one variable group
   * @param {string} groupKey - Raw `group` value from field data
   * @author Abhishek Dhillon
   */
  const toggleGroupCollapse = useCallback((groupKey) => {
    if (groupKey == null || groupKey === "") return;
    const k = String(groupKey);
    setCollapsedGroupKeys((prev) => {
      const idx = prev.indexOf(k);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, k];
    });
  }, []);

  /**
   * Handles field selection change
   * @param {Object} e - Event object
   * @author Abhishek Dhillon
   */
  const handleFieldChange = useCallback(
    (e) => {
      const newField = e.target.value;
      if (newField?.__fieldSelectionGroupCollapsed) return;
      onFieldChange(newField);
    },
    [onFieldChange]
  );

  /**
   * Gets field type hint text
   * @returns {string} Hint text for field type
   * @author Abhishek Dhillon
   */
  const getFieldTypeHint = () => {
    let hint = ``;
    if (selectedField?.linkedVarName) {
      return (
        <div className='d-flex align-items-center mb-2'>
          <span className='mr-2 grey-linkage pointer-events-none'>{icon.LOCK_ICON}</span>
          <span className='grey-linkage pointer-events-none'>Linked to {selectedField?.linkedVarName} (Global Chip)</span>
        </div>
      );
    }
    if (fieldType === "dropdown" && selectedField?.options?.length > 0) {
      hint += ` (${selectedField.options.length} options)`;
    }
    return hint;
  };

  /**
   * Renders the item for the dropdown list
   * @param {Object} li - List item
   * @param {Object} itemProps - Item properties
   * @returns {JSX.Element} Rendered item
   * @author Abhishek Dhillon
   */
  const itemRender = (li, itemProps) => {
    if (itemProps?.dataItem?.__fieldSelectionGroupCollapsed) {
      return null;
    }
    const itemChildren = (
      <span key={itemProps?.index} className='d-flex align-items-center justify-content-between w-100'>
        <span>
          {getVariableIcon(itemProps?.dataItem)} {itemProps?.dataItem?.name}
        </span>
        <small className='text-muted text-right'>
          {itemProps?.dataItem?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD && (
            <div title='Global' className={`custom-variable smart-chip-lock mr-2`}>
              {icon.LOCK_ICON}
            </div>
          )}
        </small>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const getVariableIcon = (variable) => {
    return variable?.type === WORKFLOW_VARIABLE_LABELS.DROPDOWN
      ? variable?.attributes?.numberOnly
        ? VARIABLE_ICONS.number_dropdown
        : VARIABLE_ICONS.dropdown
      : VARIABLE_ICONS[variable?.type]
      ? VARIABLE_ICONS[variable?.type]
      : VARIABLE_ICONS.dropdown;
  };

  /**
   * Handles filter change
   * @param {Object} e - Event object
   * @author Abhishek Dhillon
   */
  const handleFilterChange = (e) => {
    setFilteredFields(filterBy(availableFields, e?.filter));
  };

  const getHeaderText = (headerValue) => {
    if (!headerValue) return headerValue;
    if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.FORM_SMARTCHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.FORM_SMARTCHIPS;
    } else if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.CREATOR_CHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.CREATOR_CHIPS;
    } else if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.ASSIGNEE_CHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.ASSIGNEE_CHIPS;
    } else if (headerValue === WF_EXPRESSION_VARIABLE_GROUP_NAMES.USER_CHIPS) {
      return WF_EXPRESSION_VARIABLE_GROUP_HEADERS.USER_CHIPS;
    }
    return headerValue;
  };

  /**
   * Renders a group header row with expand/collapse control
   * @param {React.ReactElement} li - Kendo list item or sticky header wrapper
   * @param {"list" | "sticky"} variant - Distinguishes ids when list and sticky headers are both mounted
   * @returns {React.ReactElement} Cloned element
   * @author Abhishek Dhillon
   */
  const renderGroupHeaderControl = (li, variant) => {
    const children = li.props.children;
    const groupKey = children?.props?.children != null ? String(children.props.children) : "";
    const headerText = getHeaderText(groupKey);
    const isCollapsed = collapsedGroupKeySet.has(groupKey);
    const toggleId = `field-selection-group-toggle-${variant}-${getFieldSelectionGroupToggleIdFragment(groupKey)}`;

    const stopListNavigation = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const onHeaderActivate = (e) => {
      stopListNavigation(e);
      toggleGroupCollapse(groupKey);
    };

    const onHeaderKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        stopListNavigation(e);
        toggleGroupCollapse(groupKey);
      }
    };

    const itemHeader = (
      <span
        id={toggleId}
        role='button'
        tabIndex={0}
        aria-expanded={!isCollapsed}
        className='d-flex align-items-center gap-2 cursor-pointer w-100'
        onClick={onHeaderActivate}
        onKeyDown={onHeaderKeyDown}>
        <span>{isCollapsed ? icon.ARROW_DOWN : icon.ARROW_UP}</span>
        {headerText}
      </span>
    );
    return React.cloneElement(li, { ...li.props, "data-expression-group-header": groupKey }, itemHeader);
  };

  const groupHeaderItemRender = (li) => renderGroupHeaderControl(li, "list");

  const stickyHeaderRender = (div) => renderGroupHeaderControl(div, "sticky");

  return (
    <div className='expression-builder-rule-field'>
      <label>{EXPRESSION_LABELS.FIELD}</label>
      <DropDownList
        data={dropdownListData}
        textField='name'
        valueField='id'
        groupField='group'
        groupHeaderItemRender={groupHeaderItemRender}
        groupStickyHeaderItemRender={stickyHeaderRender}
        value={selectedField}
        onChange={handleFieldChange}
        placeholder={EXPRESSION_LABELS.SELECT_FIELD}
        className='expression-builder-w-100'
        id='field-selection-dropdown'
        adaptive={true}
        filterable={true}
        onFilterChange={handleFilterChange}
        itemRender={itemRender}
        fillMode={"solid"}
        required={true}
      />
      {selectedField && <Hint>{getFieldTypeHint()}</Hint>}
    </div>
  );
};

export default FieldSelection;
